import { useCallback, useEffect, useRef } from 'react';
import { singletonHook } from 'react-singleton-hook';

const WAITING_TIMEOUT = 500;

type BatchRequest<TResult> = {
  keyIds: string[];
  resolve: (value: TResult[]) => void;
  reject: (reason?: unknown) => void;
};

type CollectParams<TKey, TResult> = {
  batchKey: string;
  keys: TKey[];
  wait?: number;
  getKeyId: (key: TKey) => string;
  fetchBatch: (keys: TKey[]) => Promise<TResult[]>;
  getResultKeyId: (result: TResult) => string;
};

type Batch<TKey, TResult> = {
  keysById: Map<string, TKey>;
  requests: Array<BatchRequest<TResult>>;
  timer: ReturnType<typeof setTimeout> | null;
  wait: number;
  fetchBatch: (keys: TKey[]) => Promise<TResult[]>;
  getResultKeyId: (result: TResult) => string;
};

type AnyBatch = Batch<any, any>;

type DebouncedBatcherSingleton = {
  collect: <TKey, TResult>(params: CollectParams<TKey, TResult>) => Promise<TResult[]>;
};

const initState: DebouncedBatcherSingleton = {
  collect: async () => [],
};

const useDebouncedBatchReceiverImpl = (): DebouncedBatcherSingleton => {
  const batchesRef = useRef<Record<string, AnyBatch>>({});

  const collect = useCallback(
    <TKey, TResult>({
      batchKey,
      keys,
      wait = WAITING_TIMEOUT,
      getKeyId,
      fetchBatch,
      getResultKeyId,
    }: CollectParams<TKey, TResult>): Promise<TResult[]> => {
      if (!keys.length) {
        return Promise.resolve([]);
      }

      return new Promise<TResult[]>((resolve, reject) => {
        const existingBatch = batchesRef.current[batchKey] as Batch<TKey, TResult> | undefined;

        const batch =
          existingBatch ||
          (batchesRef.current[batchKey] = {
            keysById: new Map<string, TKey>(),
            requests: [],
            timer: null,
            wait,
            fetchBatch,
            getResultKeyId,
          } as Batch<TKey, TResult>);

        const keyIds = keys.map(getKeyId);

        keys.forEach((key, index) => {
          batch.keysById.set(keyIds[index], key);
        });

        batch.requests.push({
          keyIds,
          resolve,
          reject,
        });

        if (batch.timer) {
          clearTimeout(batch.timer);
        }

        batch.timer = setTimeout(async () => {
          const currentBatch = batchesRef.current[batchKey] as Batch<TKey, TResult> | undefined;

          if (!currentBatch) {
            return;
          }

          delete batchesRef.current[batchKey];

          try {
            const results = await currentBatch.fetchBatch([...currentBatch.keysById.values()]);
            const resultByKeyId = new Map(results.map(result => [currentBatch.getResultKeyId(result), result]));

            currentBatch.requests.forEach(request => {
              const requestResults = request.keyIds
                .map(keyId => resultByKeyId.get(keyId))
                .filter((result): result is TResult => result !== undefined);

              request.resolve(requestResults);
            });
          } catch (error) {
            currentBatch.requests.forEach(request => request.reject(error));
          }
        }, batch.wait);
      });
    },
    [],
  );

  useEffect(() => {
    return () => {
      Object.values(batchesRef.current).forEach(batch => {
        if (batch.timer) {
          clearTimeout(batch.timer);
        }

        batch.requests.forEach(request => {
          request.reject(new Error('Debounced batcher singleton unmounted'));
        });
      });

      batchesRef.current = {};
    };
  }, []);

  return { collect };
};

const useDebouncedBatchReceiver = singletonHook(initState, useDebouncedBatchReceiverImpl);

export default useDebouncedBatchReceiver;
